import { saveAs } from 'file-saver'
import { useCallback, useState } from 'react'

import { createZipWithStats, type FileToZip, type ZipResult } from '../utils/fileUtils'

export type DownloadStatus = 'idle' | 'preparing' | 'downloading' | 'success' | 'error' | 'empty'

export interface UseFileDownloadReturn {
  downloadStatus: DownloadStatus
  downloadFiles: (files: FileToZip[], filename: string, folderName?: string) => Promise<ZipResult | null>
  resetStatus: () => void
}

/**
 * Hook for downloading files as a zip archive
 * @returns Object with download status and functions
 */
export const useFileDownload = (): UseFileDownloadReturn => {
  const [downloadStatus, setDownloadStatus] = useState<DownloadStatus>('idle')

  const resetStatus = useCallback(() => {
    setDownloadStatus('idle')
  }, [])

  const downloadFiles = useCallback(async (files: FileToZip[], filename: string, folderName?: string): Promise<ZipResult | null> => {
    try {
      if (files.length === 0) {
        setDownloadStatus('empty')
        return null
      }

      setDownloadStatus('preparing')

      // Create a zip file with the provided files
      const result = await createZipWithStats(files, folderName)

      if (!result.content) {
        setDownloadStatus('empty')
        return null
      }

      setDownloadStatus('downloading')

      // Trigger the download
      saveAs(result.content, filename)

      // Update status based on download result
      if (result.stats.failed > 0 && result.stats.success > 0) {
        setDownloadStatus('success') // Partial success - we still have some downloaded files
      } else if (result.stats.failed > 0 && result.stats.success === 0) {
        setDownloadStatus('error')
      } else {
        setDownloadStatus('success')
      }

      return result
    } catch (error) {
      console.error('Error downloading files:', error)
      setDownloadStatus('error')
      return null
    }
  }, [])

  return { downloadStatus, downloadFiles, resetStatus }
}
